import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import isAdministrator from "../utilities/checkPrivileges";
import profileService, { assetIdFromImageValue } from "../services/profileService";
import compressImageFile from "../utils/compressImageFile";
import TriStateControl, { isUnset } from "../components/profileEditor/TriStateControl";
import OsVersionBadges from "../components/profileEditor/OsVersionBadges";
import FieldIcon from "../components/profileEditor/FieldIcon";
import HomeScreenLayoutEditor, { collectLayoutBundleIds } from "../components/profileEditor/HomeScreenLayoutEditor";
import NotificationsEditor from "../components/profileEditor/NotificationsEditor";
import WifiPayloadEditor from "../components/profileEditor/WifiPayloadEditor";
import { Pkcs12Editor, RootCertificateEditor } from "../components/profileEditor/CertificateEditors";
import AppLockEditor from "../components/profileEditor/AppLockEditor";
import LoginScriptsEditor from "../components/profileEditor/LoginScriptsEditor";
import "./profileEditor.css";

function emptyDoc() {
  return {
    PayloadDisplayName: "",
    PayloadDescription: "",
    PayloadOrganization: "",
    PayloadIdentifier: "",
    PayloadRemovalDisallowed: false,
    PayloadScope: "System",
    TargetDeviceType: 0,
    payloads: [],
  };
}

function newId() {
  return (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

function asScriptListSeed(value) {
  return Array.isArray(value) ? value : [];
}

const TARGET_DEVICE_OPTIONS = [
  { value: 0, label: "Alle / unbestimmt", filter: "all" },
  { value: 1, label: "iPhone / iPad", filter: "ios" },
  { value: 4, label: "Apple TV", filter: "tvos" },
  { value: 5, label: "Mac", filter: "macos" },
];

function targetFilterFromType(targetDeviceType) {
  return TARGET_DEVICE_OPTIONS.find((item) => item.value === Number(targetDeviceType))?.filter || "all";
}

function platformsMatchTarget(platforms, targetDeviceType) {
  const filter = targetFilterFromType(targetDeviceType);
  if (filter === "all") return true;
  const list = platforms || [];
  if (!list.length) return true;
  if (filter === "ios") return list.includes("ios") || list.includes("ipados");
  return list.includes(filter);
}

function exclusiveTargetFromPlatformLists(platformLists) {
  const families = new Set();
  for (const list of platformLists) {
    const platforms = list || [];
    if (!platforms.length) continue;
    const hasIos = platforms.some((p) => p === "ios" || p === "ipados");
    const hasMac = platforms.includes("macos");
    const hasTv = platforms.includes("tvos");
    if (hasIos && !hasMac && !hasTv) families.add(1);
    else if (hasTv && !hasIos && !hasMac) families.add(4);
    else if (hasMac && !hasIos && !hasTv) families.add(5);
  }
  if (families.size === 1) return [...families][0];
  if (families.size > 1) return "conflict";
  return null;
}

function Hint({ text }) {
  if (!text) return null;
  return <div className="form-text">{text}</div>;
}

function payloadMatchesFilter(item, filter) {
  if (filter === "all") return true;
  const platforms = item.platforms || [];
  if (filter === "ios") return platforms.includes("ios") || platforms.includes("ipados");
  return platforms.includes(filter);
}

function PayloadMetaBadges({ spec }) {
  if (!spec) return null;
  const labels = {
    ios: "iOS",
    ipados: "iPadOS",
    macos: "macOS",
    tvos: "tvOS",
    visionos: "visionOS",
  };
  const hasIntroduced = spec.introduced && Object.keys(spec.introduced).length;
  return (
    <div className="d-flex flex-wrap gap-1 mt-1">
      {hasIntroduced ? (
        <OsVersionBadges introduced={spec.introduced} supervised={spec.requiresSupervised === true} />
      ) : (
        (spec.platforms || []).map((platform) => (
          <span key={platform} className="profile-os-badge">{labels[platform] || platform}</span>
        ))
      )}
      {spec.requiresSupervised === "partial" ? (
        <span className="profile-os-badge is-supervised" title="Einige Schlüssel nur auf betreuten Geräten">teilw. sup</span>
      ) : null}
      {!hasIntroduced && spec.requiresSupervised === true ? (
        <span className="profile-os-badge is-supervised" title="Nur auf betreuten (supervised) Geräten">sup</span>
      ) : null}
    </div>
  );
}

function FieldLabel({ field }) {
  const description = field.appleContent || field.hint || field.label;
  return (
    <span className="profile-field-label" title={description}>
      <FieldIcon field={field} />
      {field.label}
      <OsVersionBadges introduced={field.introduced} supervised={field.appleSupervised === true} />
    </span>
  );
}

function imageSrcFromValue(value) {
  const raw = String(value || "");
  if (!raw) return "";
  if (raw.startsWith("asset:")) return "";
  return raw.startsWith("data:") ? raw : `data:image/jpeg;base64,${raw}`;
}

function previewPlaceholder(text) {
  return String(text || "")
    .replaceAll("{{FULLNAME}}", "iPad-Klasse-5b")
    .replaceAll("{{SERIALNUMBER}}", "F4XXXX1234")
    .replaceAll("{{ASSETTAG}}", "AT-001")
    .replaceAll("{{ASSIGNED-USER}}", "Max Mustermann")
    .replaceAll("{{USERNAME}}", "mmuster")
    .replaceAll("{{USER-EMAIL}}", "max@schule.de")
    .replaceAll("{{USER-ROLE}}", "student")
    .replaceAll("{{USER-CLASS}}", "5b")
    .replaceAll("{{LOCATION-NAME}}", "Standort")
    .replaceAll("{{NOTES}}", "");
}

function hexToRgba(hex, opacityPct) {
  const raw = String(hex || "#000000").replace("#", "");
  const full = raw.length === 3 ? raw.split("").map((ch) => ch + ch).join("") : raw.padEnd(6, "0").slice(0, 6);
  const r = Number.parseInt(full.slice(0, 2), 16) || 0;
  const g = Number.parseInt(full.slice(2, 4), 16) || 0;
  const b = Number.parseInt(full.slice(4, 6), 16) || 0;
  const a = Math.min(100, Math.max(0, Number(opacityPct) || 70)) / 100;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function WallpaperPreview({ fields }) {
  const raw = fields?.Image;
  const assetId = assetIdFromImageValue(raw);
  const [assetSrc, setAssetSrc] = useState("");
  useEffect(() => {
    let revoked = "";
    let cancelled = false;
    if (!assetId) {
      setAssetSrc("");
      return undefined;
    }
    profileService.assetObjectUrl(assetId).then((url) => {
      if (cancelled) {
        URL.revokeObjectURL(url);
        return;
      }
      revoked = url;
      setAssetSrc(url);
    }).catch(() => {
      if (!cancelled) setAssetSrc("");
    });
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [assetId]);
  const src = assetSrc || imageSrcFromValue(raw);
  if (!src) return null;
  const overlay = String(fields?.OverlayText || "").trim();
  const position = fields?.OverlayPosition || "bottom";
  const align = position === "top" ? "flex-start" : position === "center" ? "center" : "flex-end";
  return (
    <div className="mt-3">
      <div className="form-text mb-1">Vorschau (Platzhalter beispielhaft)</div>
      <div className="position-relative rounded border overflow-hidden" style={{ maxWidth: 280 }}>
        <img alt="Hintergrund-Vorschau" src={src} className="w-100 d-block" />
        {overlay ? (
          <div
            className="position-absolute start-0 end-0 d-flex"
            style={{
              top: 0,
              bottom: 0,
              alignItems: align,
              paddingTop: "20%",
              paddingBottom: "16%",
              boxSizing: "border-box",
              pointerEvents: "none",
            }}
          >
            <div
              className="w-100 text-center py-2 px-2"
              style={{
                background: hexToRgba(fields?.OverlayBarColor || "#000000", fields?.OverlayBarOpacity),
                color: fields?.OverlayTextColor || "#ffffff",
                fontWeight: 600,
                fontSize: Math.max(10, Math.min(18, Number(fields?.OverlayFontSize) / 4 || 12)),
                whiteSpace: "pre-line",
              }}
            >
              {previewPlaceholder(overlay)}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function jsonDisplay(value) {
  if (isUnset(value)) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function ImageField({ field, value, onChange, disabled }) {
  const [uploading, setUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState("");
  const assetId = assetIdFromImageValue(value);
  const preview = localPreview || imageSrcFromValue(value);
  const maxEdge = Number(field.maxEdge) > 0 ? Number(field.maxEdge) : 2732;
  const maxBytes = Number(field.maxBytes) > 0 ? Number(field.maxBytes) : 620 * 1024;

  useEffect(() => {
    if (!assetId) return undefined;
    let cancelled = false;
    let created = "";
    profileService.assetObjectUrl(assetId).then((url) => {
      if (cancelled) {
        URL.revokeObjectURL(url);
        return;
      }
      created = url;
      setLocalPreview(url);
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [assetId]);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const dataUrl = await compressImageFile(file, { maxEdge, quality: 0.9, maxBytes });
      setLocalPreview(dataUrl);
      const stored = await profileService.uploadAsset(dataUrl);
      onChange(stored.ref);
    } catch (err) {
      setLocalPreview("");
      onChange(undefined);
      toast.error(err.response?.data?.message || err.message || "Bild konnte nicht hochgeladen werden.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <FieldLabel field={field} />
      <input
        type="file"
        className="form-control mt-1"
        disabled={disabled || uploading}
        accept="image/png,image/jpeg,.png,.jpg,.jpeg"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          await uploadFile(file);
        }}
      />
      {uploading ? <div className="form-text">Bild wird im Hintergrund hochgeladen…</div> : null}
      {preview ? (
        <div className="d-flex align-items-center gap-2 mt-2">
          <img
            alt="Vorschau"
            className="rounded border"
            style={{ maxHeight: 96, maxWidth: "100%" }}
            src={preview}
          />
          {!field.required && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={disabled || uploading}
              onClick={() => {
                setLocalPreview("");
                onChange(undefined);
              }}
            >
              Zurücksetzen
            </button>
          )}
        </div>
      ) : null}
      <Hint text={field.hint || "Das Bild wird direkt nach der Auswahl hochgeladen, nicht erst beim Speichern des Profils."} />
    </>
  );
}

function FieldInput({ field, value, onChange, disabled }) {
  if (field.type === "boolean") {
    return (
      <div className="profile-field-row">
        <FieldLabel field={field} />
        <TriStateControl value={value} onChange={onChange} disabled={disabled} name={field.label} />
      </div>
    );
  }
  if (field.type === "select") {
    const unset = isUnset(value);
    return (
      <>
        <FieldLabel field={field} />
        <select
          className="form-select form-select-sm mt-1"
          disabled={disabled}
          value={unset ? "" : value}
          onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value)}
        >
          {!field.required && <option value="">Nicht konfiguriert</option>}
          {(field.options || []).map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <Hint text={field.hint} />
      </>
    );
  }
  if (field.type === "color") {
    const unset = isUnset(value);
    const color = unset ? (field.default || "#000000") : value;
    return (
      <>
        <FieldLabel field={field} />
        <div className="d-flex align-items-center gap-2 mt-1">
          <input
            type="color"
            className="form-control form-control-color"
            disabled={disabled}
            value={color}
            onChange={(e) => onChange(e.target.value)}
          />
          <code className="small">{color}</code>
          {!field.required && !unset && (
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={disabled} onClick={() => onChange(undefined)}>
              Zurücksetzen
            </button>
          )}
        </div>
        <Hint text={field.hint} />
      </>
    );
  }
  if (field.type === "image") {
    return <ImageField field={field} value={value} onChange={onChange} disabled={disabled} />;
  }
  if (field.type === "textarea" || field.type === "stringlist" || field.type === "json" || field.type === "bookmarks") {
    const display = field.type === "json" ? jsonDisplay(value) : (Array.isArray(value) ? value.join("\n") : (value ?? ""));
    return (
      <>
        <FieldLabel field={field} />
        <textarea
          className="form-control font-monospace small mt-1"
          rows={field.type === "json" ? 8 : field.type === "textarea" ? 5 : 3}
          disabled={disabled}
          placeholder="Nicht konfiguriert — leer lassen, um den Schlüssel wegzulassen"
          value={display}
          onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value)}
        />
        <Hint text={field.hint} />
      </>
    );
  }
  return (
    <>
      <FieldLabel field={field} />
      <input
        type={field.type === "password" ? "password" : field.type === "number" ? "number" : "text"}
        className="form-control form-control-sm mt-1"
        disabled={disabled}
        placeholder={field.required ? "" : "Nicht konfiguriert"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value)}
      />
      <Hint text={field.hint} />
    </>
  );
}

function groupedFields(fields) {
  const groups = [];
  let current = { id: "", items: [] };
  for (const field of fields) {
    const id = field.group || "";
    if (id !== current.id) {
      if (current.items.length) groups.push(current);
      current = { id, items: [field] };
    } else {
      current.items.push(field);
    }
  }
  if (current.items.length) groups.push(current);
  return groups;
}

function DefaultFields({ spec, fields, onFieldChange, disabled, targetDeviceType = 0 }) {
  const known = new Set((spec?.fields || []).map((field) => field.key));
  const visible = (spec?.fields || []).filter((field) => {
    if (field.skipInProfile || field.type === "object" || field.type === "data") return false;
    if (!platformsMatchTarget(field.platforms, targetDeviceType)) return false;
    return true;
  });
  const unmappedKeys = Object.keys(fields || {}).filter((key) => !known.has(key));
  const hasGroups = visible.some((field) => field.group);
  const catalogBlock = !hasGroups ? (
    <div className="row g-3">
      {visible.map((field) => (
        <div className={field.type === "json" || field.type === "image" || field.type === "textarea" || field.type === "bookmarks" || field.type === "stringlist" ? "col-12" : "col-md-6"} key={field.key}>
          <FieldInput field={field} value={fields?.[field.key]} onChange={(value) => onFieldChange(field.key, value)} disabled={disabled} />
        </div>
      ))}
    </div>
  ) : (
    groupedFields(visible).map((group) => (
      <div key={group.id || "default"}>
        {group.id ? <div className="profile-restriction-group">{group.id}</div> : null}
        <div className="profile-restriction-grid">
          {group.items.map((field) => (
            <div key={field.key} style={field.type === "boolean" ? undefined : { gridColumn: "1 / -1" }}>
              <FieldInput field={field} value={fields?.[field.key]} onChange={(value) => onFieldChange(field.key, value)} disabled={disabled} />
            </div>
          ))}
        </div>
      </div>
    ))
  );
  return (
    <>
      {catalogBlock}
      {unmappedKeys.length ? (
        <UnmappedImportedFields
          keys={unmappedKeys}
          fields={fields}
          disabled={disabled}
          onFieldChange={onFieldChange}
        />
      ) : null}
    </>
  );
}

function UnmappedImportedFields({ keys, fields, disabled, onFieldChange }) {
  return (
    <div className="profile-import-unmapped mt-3">
      <div className="profile-restriction-group">Importierte Zusatzschlüssel</div>
      <div className="small text-muted mb-2">
        Diese Schlüssel sind in der mobileconfig vorhanden, aber noch nicht als eigene Editor-Felder kuratiert.
        Werte bleiben beim Speichern erhalten.
      </div>
      <div className="profile-restriction-grid">
        {keys.map((key) => {
          const value = fields?.[key];
          const isBool = value === true || value === false;
          const field = {
            key,
            label: key,
            type: isBool ? "boolean" : (typeof value === "number" ? "number" : (typeof value === "object" ? "json" : "string")),
            hint: "Nicht kuratierter Import-Schlüssel",
          };
          return (
            <div key={key} style={isBool ? undefined : { gridColumn: "1 / -1" }}>
              <FieldInput field={field} value={value} onChange={(next) => onFieldChange(key, next)} disabled={disabled} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function applyAllowlistFromLayout(payloads, layoutFields) {
  if (!layoutFields?.syncAppAccessWithLayout) return payloads;
  const list = collectLayoutBundleIds(layoutFields).join("\n");
  const index = payloads.findIndex((item) => item.type === "com.apple.applicationaccess");
  if (index === -1) {
    return [
      ...payloads,
      {
        id: newId(),
        uuid: newId(),
        type: "com.apple.applicationaccess",
        displayName: "Einschränkungen",
        fields: { allowListedAppBundleIDs: list },
      },
    ];
  }
  return payloads.map((item, i) => (
    i === index
      ? { ...item, fields: { ...item.fields, allowListedAppBundleIDs: list } }
      : item
  ));
}

function OpaquePayloadEditor({ fields, onChange, disabled }) {
  const [text, setText] = useState(() => JSON.stringify(fields || {}, null, 2));
  useEffect(() => {
    setText(JSON.stringify(fields || {}, null, 2));
  }, [fields]);
  return (
    <>
      <div className="small text-muted mb-2">
        Dieser Payload-Typ hat noch keine eigene Maske. Die Schlüssel bleiben beim Speichern erhalten.
      </div>
      <textarea
        className="form-control font-monospace small"
        rows={10}
        disabled={disabled}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            onChange(JSON.parse(e.target.value || "{}"));
          } catch {
            /* solange JSON ungültig ist, Felder nicht überschreiben */
          }
        }}
      />
    </>
  );
}

function PayloadBody({ spec, payload, doc, disabled, onFieldsChange }) {
  const fields = payload.fields || {};
  if (!spec || payload.opaque) {
    return <OpaquePayloadEditor fields={fields} disabled={disabled} onChange={onFieldsChange} />;
  }
  if (spec?.editor === "wifi") {
    const identityCerts = doc.payloads
      .filter((item) => item.type === "com.apple.security.pkcs12")
      .map((item) => ({
        uuid: item.uuid,
        name: item.fields?.PayloadCertificateFileName || item.displayName || "PKCS#12",
      }));
    const trustCerts = doc.payloads
      .filter((item) => item.type === "com.apple.security.root")
      .map((item) => ({
        uuid: item.uuid,
        name: item.fields?.PayloadCertificateFileName || item.displayName || "Stammzertifikat",
      }));
    return (
      <WifiPayloadEditor
        fields={fields}
        disabled={disabled}
        identityCerts={identityCerts}
        trustCerts={trustCerts}
        onChange={onFieldsChange}
      />
    );
  }
  if (spec?.editor === "homescreen") {
    const webclips = doc.payloads
      .filter((item) => item.type === "com.apple.webClip.managed")
      .map((item) => ({ Label: item.fields?.Label, URL: item.fields?.URL }))
      .filter((item) => item.URL);
    return (
      <HomeScreenLayoutEditor
        fields={fields}
        disabled={disabled}
        webclips={webclips}
        onChange={onFieldsChange}
      />
    );
  }
  if (spec?.editor === "notifications") {
    return <NotificationsEditor fields={fields} disabled={disabled} onChange={onFieldsChange} />;
  }
  if (spec?.editor === "pkcs12") {
    return <Pkcs12Editor fields={fields} disabled={disabled} onChange={onFieldsChange} />;
  }
  if (spec?.editor === "rootcert") {
    return <RootCertificateEditor fields={fields} disabled={disabled} onChange={onFieldsChange} />;
  }
  if (spec?.editor === "applock") {
    return <AppLockEditor fields={fields} disabled={disabled} onChange={onFieldsChange} />;
  }
  if (spec?.editor === "loginscripts" || payload.type === "com.apple.mcxloginscripts") {
    return <LoginScriptsEditor fields={fields} disabled={disabled} onChange={onFieldsChange} />;
  }
  return (
    <DefaultFields
      spec={spec}
      fields={fields}
      disabled={disabled}
      targetDeviceType={doc.TargetDeviceType}
      onFieldChange={(key, value) => {
        const next = { ...fields };
        if (value === undefined || value === null || value === "") delete next[key];
        else next[key] = value;
        onFieldsChange(next);
      }}
    />
  );
}

export default function ProfileEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const admin = isAdministrator();
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [appleSource, setAppleSource] = useState("");
  const [doc, setDoc] = useState(emptyDoc);
  const [saving, setSaving] = useState(false);
  const [addType, setAddType] = useState("");
  const [catalogQuery, setCatalogQuery] = useState("");
  const [importWarnings, setImportWarnings] = useState([]);
  const [catalogMeta, setCatalogMeta] = useState(null);
  const importInput = useRef(null);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    profileService.getCatalog()
      .then((data) => {
        setCatalog(data.payloads || []);
        setCategories(data.categories || []);
        setAppleSource(data.appleSource || "");
        setCatalogMeta({
          source: data.appleCatalogSource,
          count: data.applePayloadCount ?? data.payloads?.length,
          ref: data.appleRef,
        });
        setAddType(data.payloads?.[0]?.type || "");
      })
      .catch((err) => toast.error(err.response?.data?.message || "Katalog konnte nicht geladen werden."));
  }, []);

  useEffect(() => {
    if (!id) {
      setDoc(emptyDoc());
      setImportWarnings([]);
      return;
    }
    profileService.getProfile(id)
      .then((row) => {
        if (row.importError) {
          toast.error(row.importError);
        }
        const warnings = row.importWarnings?.length
          ? row.importWarnings
          : (row.editorDocument?.importWarnings || []);
        setImportWarnings(warnings);
        if (warnings.length) {
          toast.info(warnings.join(" "));
        }
        if (row.editorDocument) {
          const next = { ...emptyDoc(), ...row.editorDocument };
          next.payloads = (next.payloads || []).map((item) => ({ ...item, uuid: item.uuid || newId() }));
          setDoc(next);
        } else {
          setDoc({
            ...emptyDoc(),
            PayloadDisplayName: row.PayloadDisplayName || "",
            PayloadDescription: row.PayloadDescription || "",
            PayloadOrganization: row.PayloadOrganization || "",
            PayloadIdentifier: row.PayloadIdentifier || "",
          });
          toast.info("Dieses Profil konnte nicht vollständig in den Editor übernommen werden. Die XML-Ansicht bleibt verfügbar.");
        }
      })
      .catch((err) => toast.error(err.response?.data?.message || "Profil nicht gefunden."));
  }, [id]);

  const specByType = useMemo(() => new Map(catalog.map((item) => [item.type, item])), [catalog]);

  const platformFilter = targetFilterFromType(doc.TargetDeviceType);

  const requiredTarget = useMemo(() => {
    const platformLists = [];
    for (const payload of doc.payloads || []) {
      const spec = specByType.get(payload.type);
      const payloadFamily = spec?.platforms?.length
        ? exclusiveTargetFromPlatformLists([spec.platforms])
        : null;
      // Payload mit mehreren Gerätefamilien (z. B. Hintergrundbild iOS+Mac) erzwingt kein Ziel.
      if (typeof payloadFamily === "number") platformLists.push(spec.platforms);
      if (payloadFamily === "conflict") continue;
      for (const field of spec?.fields || []) {
        if (payload.fields?.[field.key] === undefined) continue;
        if (!field.platforms?.length) continue;
        // iOS-only-Unterfelder (z. B. Where) nicht gegen ein gewähltes Mac-Ziel rechnen
        if (
          Number(doc.TargetDeviceType) !== 0
          && !platformsMatchTarget(field.platforms, doc.TargetDeviceType)
        ) {
          continue;
        }
        platformLists.push(field.platforms);
      }
    }
    return exclusiveTargetFromPlatformLists(platformLists);
  }, [doc.payloads, doc.TargetDeviceType, specByType]);

  const targetRequired = requiredTarget !== null;
  const targetConflict = requiredTarget === "conflict";
  const targetMismatch = typeof requiredTarget === "number"
    && Number(doc.TargetDeviceType) !== 0
    && Number(doc.TargetDeviceType) !== requiredTarget;

  const filteredCatalog = useMemo(() => catalog.filter((item) => payloadMatchesFilter(item, platformFilter)), [catalog, platformFilter]);

  const groupedCatalog = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    const match = (item) => !q
      || String(item.label || "").toLowerCase().includes(q)
      || String(item.type || "").toLowerCase().includes(q)
      || String(item.description || "").toLowerCase().includes(q);
    const groups = [];
    const seen = new Set();
    for (const category of categories) {
      const items = filteredCatalog.filter((item) => item.category === category.id && match(item));
      if (items.length) {
        groups.push({ id: category.id, label: category.label, items });
        items.forEach((item) => seen.add(item.type));
      }
    }
    const rest = filteredCatalog.filter((item) => !seen.has(item.type) && match(item));
    if (rest.length) groups.push({ id: "weitere", label: "Weitere", items: rest });
    return groups;
  }, [filteredCatalog, categories, catalogQuery]);

  useEffect(() => {
    if (!filteredCatalog.some((item) => item.type === addType)) {
      setAddType(filteredCatalog[0]?.type || "");
    }
  }, [filteredCatalog, addType]);

  const setMeta = (key, value) => setDoc((prev) => ({ ...prev, [key]: value }));

  const addPayload = () => {
    if (!addType) return;
    const spec = specByType.get(addType);
    const fields = {};
    for (const field of spec?.fields || []) {
      if (field.default === undefined) continue;
      const fromRequiredOrEditor = field.required
        || spec.editor === "homescreen"
        || spec.editor === "notifications"
        || spec.editor === "loginscripts";
      // optionale Defaults mit platforms (z. B. Where) nur setzen, wenn Ziel passt
      const fromPlatformDefault = Boolean(field.platforms?.length);
      if (!fromRequiredOrEditor && !fromPlatformDefault) continue;
      if (
        field.platforms?.length
        && Number(doc.TargetDeviceType) !== 0
        && !platformsMatchTarget(field.platforms, doc.TargetDeviceType)
      ) {
        continue;
      }
      fields[field.key] = field.default;
    }
    if (addType === "com.apple.mcxloginscripts" && !asScriptListSeed(fields.LoginScripts).length) {
      fields.LoginScripts = [{
        filename: "login.sh",
        scriptText: "#!/bin/bash\nset -euo pipefail\n",
      }];
    }
    setDoc((prev) => ({
      ...prev,
      payloads: [...prev.payloads, {
        id: newId(),
        uuid: newId(),
        type: addType,
        displayName: spec?.label || addType,
        fields,
      }],
    }));
  };

  const updatePayloadFields = (index, nextFields) => {
    setDoc((prev) => {
      let payloads = prev.payloads.map((item, i) => (i === index ? { ...item, fields: nextFields } : item));
      if (payloads[index]?.type === "com.apple.homescreenlayout") {
        payloads = applyAllowlistFromLayout(payloads, nextFields);
      }
      return { ...prev, payloads };
    });
  };

  const removePayload = (index) => {
    setDoc((prev) => ({ ...prev, payloads: prev.payloads.filter((_, i) => i !== index) }));
  };

  const importMobileconfigFile = async (file) => {
    if (!file) return;
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const text = String(reader.result || "");
          const comma = text.indexOf(",");
          resolve(comma >= 0 ? text.slice(comma + 1) : text);
        };
        reader.onerror = () => reject(reader.error || new Error("Datei konnte nicht gelesen werden"));
        reader.readAsDataURL(file);
      });
      const parsed = await profileService.parseMobileConfig(base64);
      const next = { ...emptyDoc(), ...(parsed.editorDocument || {}) };
      next.payloads = (next.payloads || []).map((item) => ({ ...item, uuid: item.uuid || newId(), id: item.id || newId() }));
      setDoc(next);
      const warnings = parsed.warnings?.length ? parsed.warnings : (parsed.editorDocument?.importWarnings || []);
      setImportWarnings(warnings);
      if (warnings.length) toast.info(warnings.join(" "));
      else toast.success("mobileconfig in den Editor übernommen.");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Profil konnte nicht importiert werden.");
    }
  };

  const save = async () => {
    if (!doc.PayloadDisplayName.trim()) {
      toast.error("Anzeigename ist erforderlich.");
      return;
    }
    if (targetConflict) {
      toast.error("Dieses Profil mischt plattformspezifische Einstellungen mehrerer Gerätefamilien. Bitte trennen oder Zielgerät klären.");
      return;
    }
    if (targetRequired && Number(doc.TargetDeviceType) === 0) {
      const label = TARGET_DEVICE_OPTIONS.find((item) => item.value === requiredTarget)?.label || "Zielgerät";
      toast.error(`Zielgerät ist erforderlich. Bitte „${label}“ wählen.`);
      return;
    }
    if (targetMismatch) {
      const label = TARGET_DEVICE_OPTIONS.find((item) => item.value === requiredTarget)?.label || "Zielgerät";
      toast.error(`Zielgerät passt nicht zu den gesetzten Schlüsseln. Erwartet: ${label}.`);
      return;
    }
    setSaving(true);
    try {
      if (id) {
        await profileService.updateProfile(id, doc);
        toast.success("Profil gespeichert und an zugewiesene Geräte gesendet.");
      } else {
        const created = await profileService.createProfile(doc);
        toast.success("Profil angelegt.");
        navigate(`/profiles/editor/${created._id}`, { replace: true });
      }
    } catch (err) {
      const network = err.message === "Network Error" || err.code === "ERR_NETWORK";
      toast.error(network
        ? "Speichern fehlgeschlagen: die Anfrage war zu groß oder der Server nicht erreichbar."
        : (err.response?.data?.message || err.message || "Speichern fehlgeschlagen."));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="profile-editor">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h2 className="mb-1">{id ? "Profil bearbeiten" : "Neues Konfigurationsprofil"}</h2>
          <p className="text-muted mb-0">
            Xavier speichert das Profil unsigniert. Beim Installieren wird es mit dem Organisationszertifikat aus den Einstellungen signiert (nicht mit APNs).
            Optionale Schlüssel bleiben bei <strong>Nicht konfiguriert</strong> weg.
            {appleSource ? (
              <> Payload-Katalog aus <a href={appleSource} target="_blank" rel="noreferrer">apple/device-management</a>
                {catalogMeta?.count != null ? ` (${catalogMeta.count} Typen${catalogMeta.source ? `, ${catalogMeta.source}` : ""})` : ""}.
              </>
            ) : null}
          </p>
        </div>
        <div className="d-flex gap-2">
          {admin ? (
            <>
              <input
                ref={importInput}
                type="file"
                accept=".mobileconfig,.plist,application/x-apple-aspen-config"
                className="d-none"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  importMobileconfigFile(file);
                }}
              />
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => importInput.current?.click()}>
                mobileconfig importieren
              </button>
            </>
          ) : null}
          <Link to="/profiles" className="btn btn-outline-secondary btn-sm">Zur Bibliothek</Link>
        </div>
      </div>

      {importWarnings.length ? (
        <div className="profile-import-warnings mb-3" role="status">
          <div className="fw-semibold mb-1">Hinweise zur mobileconfig-Übernahme</div>
          <ul className="mb-0 ps-3">
            {importWarnings.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={() => setImportWarnings([])}>
            Ausblenden
          </button>
        </div>
      ) : null}

      <div className="card card-body mb-3">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Anzeigename</label>
            <input className="form-control" value={doc.PayloadDisplayName} onChange={(e) => setMeta("PayloadDisplayName", e.target.value)} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Organisation</label>
            <input className="form-control" value={doc.PayloadOrganization} onChange={(e) => setMeta("PayloadOrganization", e.target.value)} />
          </div>
          <div className="col-12">
            <label className="form-label">Beschreibung</label>
            <input className="form-control" value={doc.PayloadDescription} onChange={(e) => setMeta("PayloadDescription", e.target.value)} />
          </div>
          <div className="col-12">
            <label className="form-label">
              Zielgerät (TargetDeviceType)
              {targetRequired ? <span className="text-danger"> *</span> : null}
            </label>
            <div className="btn-group flex-wrap" role="group">
              {TARGET_DEVICE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`btn btn-sm ${Number(doc.TargetDeviceType) === option.value ? "btn-primary" : "btn-outline-primary"}`}
                  disabled={!admin}
                  onClick={() => setMeta("TargetDeviceType", option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className={`form-text ${targetRequired && Number(doc.TargetDeviceType) === 0 ? "text-danger" : ""}`}>
              Apple-Schlüssel im Profil. Verhindert Fehlinstallationen und filtert die Editor-Maske.
              {targetRequired && typeof requiredTarget === "number"
                ? ` Wegen plattformspezifischer Einstellungen ist „${TARGET_DEVICE_OPTIONS.find((item) => item.value === requiredTarget)?.label}“ erforderlich.`
                : null}
              {targetConflict ? " Konflikt: gemischte Gerätefamilien in einem Profil." : null}
              {targetMismatch ? " Die aktuelle Auswahl passt nicht zu den gesetzten Schlüsseln." : null}
            </div>
          </div>
          <div className="col-md-6">
            <label className="form-check mt-2">
              <input type="checkbox" className="form-check-input" checked={doc.PayloadRemovalDisallowed} onChange={(e) => setMeta("PayloadRemovalDisallowed", e.target.checked)} />
              <span className="form-check-label">Entfernen durch den Benutzer verbieten</span>
            </label>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <div className="flex-grow-1">
          <label className="form-label">Payload hinzufügen{platformFilter !== "all" ? ` (${TARGET_DEVICE_OPTIONS.find((item) => item.value === Number(doc.TargetDeviceType))?.label})` : ""}</label>
          <input
            type="search"
            className="form-control form-control-sm mb-2"
            placeholder="Payload suchen (Name oder Typ)…"
            value={catalogQuery}
            onChange={(e) => setCatalogQuery(e.target.value)}
          />
          <select className="form-select" value={addType} onChange={(e) => setAddType(e.target.value)}>
            {groupedCatalog.map((group) => (
              <optgroup key={group.id} label={group.label}>
                {group.items.map((item) => (
                  <option key={item.type} value={item.type}>
                    {item.label}{item.requiresSupervised === true ? " (Supervised)" : ""}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {platformFilter !== "macos" && platformFilter !== "all" ? (
            <div className="form-text">
              Kategorie „Skripte (Mac)“ erscheint nur bei Zielgerät „Mac“ oder „Alle / unbestimmt“.
            </div>
          ) : null}
        </div>
        <button type="button" className="btn btn-outline-primary" onClick={addPayload} disabled={!admin}>Hinzufügen</button>
      </div>

      {doc.payloads.map((payload, index) => {
        const spec = specByType.get(payload.type);
        return (
          <div className="card mb-3" key={payload.id || index}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h6 className="mb-0">{spec?.label || payload.displayName || payload.type}</h6>
                  <div className="small text-muted">
                    {spec?.description || (payload.opaque ? "Importierter Payload ohne eigene Maske." : "")}
                  </div>
                  <PayloadMetaBadges spec={spec} />
                  {spec && !spec.editor ? (
                    <div className="profile-hint-once mt-1">
                      Nicht konfiguriert lässt den Schlüssel weg. Aktiviert schreibt true, Deaktiviert false.
                    </div>
                  ) : null}
                </div>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removePayload(index)}>Entfernen</button>
              </div>
              <PayloadBody
                spec={spec}
                payload={payload}
                doc={doc}
                disabled={!admin}
                onFieldsChange={(next) => updatePayloadFields(index, next)}
              />
              {payload.type === "xavier.mdm.wallpaper" ? <WallpaperPreview fields={payload.fields || {}} /> : null}
            </div>
          </div>
        );
      })}

      <button type="button" className="btn btn-primary" disabled={saving || !admin} onClick={save}>
        {saving ? "Speichere…" : "Speichern"}
      </button>
    </div>
  );
}
